/**
 * Workstream Z.4 — VoicePanel UI parity (orb + transcript log + settings)
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ [orb] state-label  · 🎤 Click  · type to speak ⏎  ▾  ⚙        │
 *   └────────────────────────────────────────────────────────────────┘
 *      ↓ when ▾ expanded
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │ USER: launch two codex terminals                              │
 *   │ BRIDGE: Spawned two codex terminals.                          │
 *   │ ...                                              [clear]      │
 *   └────────────────────────────────────────────────────────────────┘
 *
 * Settings ⚙ → modal: voice / speed / language / activation. Persist to
 * localStorage['vibe_voice_settings']. Z.6 will hook activation into the
 * mic flow; Z.4 just wires the storage + UI affordance.
 *
 * Reviewer notes:
 *   - Orb pulse: pure CSS @keyframes (animate-pulse from Tailwind), no JS
 *     ticking. Avoids redraw cost; reads zustand status only.
 *   - Transcript log capped at 50 turns in voiceStore (HISTORY_MAX_TURNS).
 */
import { useEffect, useRef, useState } from 'react';
import {
  isLockdownLikelyBlocked,
  useVoiceStore,
  type VoiceModelOption,
  type VoicePreset,
  type VoicePresetId,
  type VoiceSettings,
  type VoiceStatus,
} from '@/stores/voiceStore';
import { useVoiceTabsStore } from '@/stores/voiceTabsStore';
import { useTenantStore } from '@/stores/tenantStore';
import { useUIStore } from '@/stores/uiStore';
import { VoiceTabsStrip } from './VoiceTabsStrip';
import { VoiceControlsBar } from './VoiceControlsBar';
import { ToolProgressStrip } from './ToolProgressStrip';
import { VoiceHistoryLog } from './VoiceHistoryLog';

// W3 (2026-05-25): export so VoiceSettingsModal.audioVolume.test.tsx can
// mount it directly without driving the full VoicePanel through OIDC + WS.
export function VoiceSettingsModal({
  settings,
  voices,
  models,
  status,
  onClose,
  onChange,
  onTeardownStreaming,
}: {
  settings: VoiceSettings;
  voices: VoicePreset[];
  models: VoiceModelOption[];
  status: VoiceStatus;
  onClose: () => void;
  onChange: (patch: Partial<VoiceSettings>) => void;
  onTeardownStreaming: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={onClose}
      data-testid="voice-settings-modal"
    >
      <div
        className="bg-vibe-bg-secondary border border-vibe-border rounded p-6 min-w-[420px] max-w-[600px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="voice-settings-modal-panel"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-vibe-text font-semibold">Voice settings</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="close settings"
            className="text-vibe-text-secondary hover:text-vibe-text text-lg"
          >
            ×
          </button>
        </div>

        <div className="space-y-3 text-sm">
          <label className="block">
            <span className="text-vibe-text-secondary">Voice</span>
            <select
              value={settings.voice}
              onChange={(e) => onChange({ voice: e.target.value as VoicePresetId })}
              data-testid="voice-settings-voice"
              className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
            >
              {voices.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.id}{v.gender ? ` (${v.gender})` : ''}{v.description ? ` — ${v.description}` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-vibe-text-secondary">
              Speaking speed: <span className="font-mono">{settings.speed.toFixed(2)}×</span>
            </span>
            <input
              type="range"
              min={0.5}
              max={2.0}
              step={0.05}
              value={settings.speed}
              onChange={(e) => onChange({ speed: Number(e.target.value) })}
              data-testid="voice-settings-speed"
              className="mt-1 block w-full"
            />
          </label>

          {/* W3 (2026-05-25) — in-app voice volume slider per D-W3-5 LOCKED.
              Range [0, 1.5]; 1.0 = native, 1.5 = +50% boost (Web Audio API gain).
              Independent of OS/system volume per user verbatim. */}
          <label className="block">
            <span className="text-vibe-text-secondary">
              App volume: <span className="font-mono">{Math.round(settings.audioVolume * 100)}%</span>
            </span>
            <input
              type="range"
              min={0}
              max={1.5}
              step={0.05}
              value={settings.audioVolume}
              onChange={(e) => onChange({ audioVolume: Number(e.target.value) })}
              data-testid="voice-settings-audio-volume"
              className="mt-1 block w-full"
            />
          </label>

          <label className="block">
            <span className="text-vibe-text-secondary">Speech recognition language</span>
            <select
              value={settings.language}
              onChange={(e) => onChange({ language: e.target.value })}
              data-testid="voice-settings-language"
              className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
            >
              <option value="auto">Auto-detect</option>
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="pt">Portuguese</option>
              <option value="it">Italian</option>
              <option value="ja">Japanese</option>
              <option value="ko">Korean</option>
              <option value="zh">Chinese</option>
            </select>
          </label>

          <label className="block">
            <span className="text-vibe-text-secondary">Activation mode</span>
            <select
              value={settings.activation}
              onChange={(e) =>
                onChange({ activation: e.target.value as 'click-toggle' | 'push-to-talk' })
              }
              data-testid="voice-settings-activation"
              className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
            >
              <option value="click-toggle">Click to toggle (current)</option>
              <option value="push-to-talk">Push-to-talk (Z.6: armed VAD)</option>
            </select>
          </label>

          <label className="block">
            <span className="text-vibe-text-secondary">Voice mode</span>
            <select
              value={settings.voiceMode}
              disabled={status !== 'idle'}
              onChange={(e) => {
                const newMode = e.target.value as 'classic' | 'streaming';
                const oldMode = settings.voiceMode;
                if (oldMode === 'streaming' && newMode !== 'streaming') {
                  // δ⁗ — leaving streaming: tear down the persistent WS so it
                  // doesn't sit on the server until IDLE_TIMEOUT_SECONDS.
                  onTeardownStreaming();
                }
                onChange({ voiceMode: newMode });
              }}
              data-testid="voice-settings-voice-mode"
              className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="classic">Click to Record (Classic)</option>
              <option value="streaming">Streaming Conversation (Experimental)</option>
            </select>
            {settings.voiceMode === 'streaming' && (
              <p className="text-xs text-vibe-text-secondary mt-1">
                δ′ streaming pipeline — per-sentence MP3 over /api/voice/turn.
                Faster first-audio but experimental; classic mode is the proven fallback.
              </p>
            )}
          </label>

          {settings.voiceMode === 'streaming' && (
            <label className="block">
              <span className="text-vibe-text-secondary">
                Auto-stop after silence (sec) — 0 disables
              </span>
              <input
                type="number"
                min={0}
                max={10}
                step={0.1}
                value={settings.streamingSilenceSec}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  if (!Number.isFinite(n) || n < 0 || n > 10) return;
                  onChange({ streamingSilenceSec: n });
                }}
                data-testid="voice-settings-streaming-silence-sec"
                className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
              />
              <p className="text-xs text-vibe-text-secondary mt-1">
                After you stop speaking for this many seconds, the turn ends
                automatically so the conversation flows without a second click.
                Default 2.5s. Set 0 for manual click-to-stop only.
              </p>
            </label>
          )}

          {/* 1.161.0 — agent identity + conversation memory */}
          <div className="pt-3 mt-2 border-t border-vibe-border">
            <h3 className="text-vibe-text-secondary text-xs uppercase tracking-wide mb-2">
              Agent identity & memory
            </h3>

            {/* 2026-06-28 — voice LLM model picker (mirrors Pi Chat's model
                selector). 'auto' = router-decides default. A concrete
                settings.model not present in the fetched list (stale pick or
                list not yet loaded) gets its own option so the control reflects
                the real value instead of silently snapping to Auto. */}
            <label className="block mb-2">
              <span className="text-vibe-text-secondary">LLM model</span>
              <select
                value={settings.model}
                onChange={(e) => onChange({ model: e.target.value })}
                data-testid="voice-settings-llm-model"
                className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
              >
                <option value="auto">Auto (router decides)</option>
                {settings.model !== 'auto'
                  && !models.some((m) => m.id === settings.model) && (
                  <option value={settings.model}>{settings.model}</option>
                )}
                {models.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
              <p className="text-xs text-vibe-text-secondary mt-1">
                Which tenant LLM router model answers your voice turns. "Auto"
                uses the router's active default (current behavior). Persists
                per voice tab.
              </p>
            </label>

            {/* 1.421.0 — unrestricted-conversation toggle (operator request
                2026-07-03). Threads unrestricted_content into dispatch/WS
                turns; backend appends the OPEN CONVERSATION prompt block.
                Whether the model actually engages is the model's own
                behavior — pick an abliterated model above for full effect.
                No model detection, no filter. Per-tab, default off. */}
            <label className="flex items-start gap-2 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.unrestrictedContent}
                onChange={() =>
                  onChange({ unrestrictedContent: !settings.unrestrictedContent })
                }
                data-testid="voice-settings-unrestricted"
                className="mt-0.5"
              />
              <span>
                <span className="text-vibe-text-secondary">
                  Unrestricted conversation
                </span>
                <p className="text-xs text-vibe-text-secondary mt-1">
                  Tells the voice agent no topic is off limits — adult and
                  NSFW conversation included. Meant for abliterated models
                  (pick one above). Persists per voice tab. Default off.
                </p>
              </span>
            </label>

            <label className="block mb-2">
              <span className="text-vibe-text-secondary">Voice agent name</span>
              <input
                type="text"
                value={settings.voiceAgentName}
                maxLength={50}
                onChange={(e) => onChange({ voiceAgentName: e.target.value })}
                data-testid="voice-settings-agent-name"
                className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
              />
              <p className="text-xs text-vibe-text-secondary mt-1">
                Used in the chat-log label and in the system prompt that
                tells the LLM what to call itself.
              </p>
            </label>

            <label className="block">
              <span className="text-vibe-text-secondary">
                Conversation memory (turns) — 0 disables
              </span>
              <input
                type="number"
                min={0}
                max={50}
                step={1}
                value={settings.dispatcherHistoryTurns}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isFinite(n) || n < 0 || n > 50) return;
                  onChange({ dispatcherHistoryTurns: n });
                }}
                data-testid="voice-settings-dispatcher-history-turns"
                className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
              />
              <p className="text-xs text-vibe-text-secondary mt-1">
                How many prior user/agent turns to include in the LLM
                context window. Higher = more memory, larger prompt.
                Default 20.
              </p>
            </label>
          </div>

          {/* 1.162.2 — voice → Pi chat delegation */}
          <div className="pt-3 mt-2 border-t border-vibe-border">
            <h3 className="text-vibe-text-secondary text-xs uppercase tracking-wide mb-2">
              Pi chat delegation
            </h3>

            <label className="block mb-2">
              <span className="text-vibe-text-secondary">Pi chat prefix phrase</span>
              <input
                type="text"
                value={settings.piPrefixPhrase}
                maxLength={20}
                onChange={(e) => onChange({ piPrefixPhrase: e.target.value })}
                data-testid="voice-settings-pi-prefix-phrase"
                className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
              />
              <p className="text-xs text-vibe-text-secondary mt-1">
                When your spoken turn starts with this phrase (followed by
                comma/space/punctuation), the SPA forwards the rest
                directly to the active Pi chat tab and skips the LLM.
                Empty disables. Default "pi chat".
              </p>
            </label>

            <label className="block">
              <span className="text-vibe-text-secondary">
                Pi chat history in voice context (turns) — 0 disables
              </span>
              <input
                type="number"
                min={0}
                max={20}
                step={1}
                value={settings.piChatHistoryTurns}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isFinite(n) || n < 0 || n > 20) return;
                  onChange({ piChatHistoryTurns: n });
                }}
                data-testid="voice-settings-pi-chat-history-turns"
                className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
              />
              <p className="text-xs text-vibe-text-secondary mt-1">
                How many recent Pi chat messages to include in the voice
                agent's context window. Lets the voice agent answer
                questions like "what did the pi agent just do?". HTTP/
                classic mode only (streaming-mode deferred). Default 10.
              </p>
            </label>

            <label className="block mt-3 mb-2">
              <span className="text-vibe-text-secondary">
                Forward-trigger phrase
              </span>
              <input
                type="text"
                value={settings.piForwardTriggerPhrase}
                maxLength={40}
                onChange={(e) => onChange({ piForwardTriggerPhrase: e.target.value })}
                data-testid="voice-settings-pi-forward-trigger-phrase"
                className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
              />
              <p className="text-xs text-vibe-text-secondary mt-1">
                When your spoken turn CONTAINS this phrase (anywhere,
                case-insensitive), the previously-said content
                (per "Forward scope" below) is forwarded to Pi chat
                instead of running through the LLM. Empty disables.
                Default "send that to pi".
              </p>
            </label>

            <label className="block">
              <span className="text-vibe-text-secondary">Forward scope</span>
              <select
                value={settings.piForwardScope}
                onChange={(e) =>
                  onChange({
                    piForwardScope: e.target.value as
                      | 'latest-bridge'
                      | 'latest-pair'
                      | 'whole-transcript',
                  })
                }
                data-testid="voice-settings-pi-forward-scope"
                className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
              >
                <option value="latest-bridge">Latest bridge reply only</option>
                <option value="latest-pair">Latest user + bridge pair</option>
                <option value="whole-transcript">Whole voice transcript</option>
              </select>
              <p className="text-xs text-vibe-text-secondary mt-1">
                What gets forwarded when the trigger phrase fires.
                Per-turn "→ Pi" buttons always send just that turn
                (independent of this setting).
              </p>
            </label>
          </div>

          {/* 1.160.0 — initial greeting on page load */}
          <div className="pt-3 mt-2 border-t border-vibe-border">
            <h3 className="text-vibe-text-secondary text-xs uppercase tracking-wide mb-2">
              Initial greeting
            </h3>

            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={settings.initialGreetingEnabled}
                onChange={(e) => onChange({ initialGreetingEnabled: e.target.checked })}
                data-testid="voice-settings-greeting-enabled"
              />
              <span className="text-vibe-text">Greet on page load</span>
            </label>

            {settings.initialGreetingEnabled && (
              <>
                <label className="block mb-2">
                  <span className="text-vibe-text-secondary">Timing</span>
                  <select
                    value={settings.initialGreetingTiming}
                    onChange={(e) =>
                      onChange({
                        initialGreetingTiming:
                          e.target.value as 'before-interaction' | 'after-interaction',
                      })
                    }
                    data-testid="voice-settings-greeting-timing"
                    className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
                  >
                    <option value="after-interaction">After first tap (works everywhere)</option>
                    <option value="before-interaction">Before first tap (desktop only)</option>
                  </select>
                </label>

                <label className="block mb-2">
                  <span className="text-vibe-text-secondary">Greeting text</span>
                  <textarea
                    value={settings.initialGreetingText}
                    onChange={(e) => onChange({ initialGreetingText: e.target.value })}
                    data-testid="voice-settings-greeting-text"
                    rows={2}
                    className="mt-1 block w-full px-2 py-1 rounded border border-vibe-border bg-vibe-bg text-vibe-text"
                  />
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.initialGreetingAutoArmMic}
                    onChange={(e) =>
                      onChange({ initialGreetingAutoArmMic: e.target.checked })
                    }
                    data-testid="voice-settings-greeting-auto-arm-mic"
                  />
                  <span className="text-vibe-text">Auto-arm mic after greeting</span>
                </label>
              </>
            )}
          </div>

          {/* 1.164.0 (WS-A.5) — per-tab "behavior when this tab loses focus".
              Defaults all false: each tab is an independent surface; switching
              away preserves audio/recorder/status by default. Users can opt
              into teardown per-tab. */}
          <div className="pt-3 mt-2 border-t border-vibe-border">
            <h3 className="text-vibe-text-secondary text-xs uppercase tracking-wide mb-2">
              When this tab loses focus
            </h3>
            <p className="text-xs text-vibe-text-secondary mb-2">
              By default, this tab keeps speaking + its status when you switch
              to another voice tab (active recorders are always released).
              Enable below to override.
            </p>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={settings.pauseAudioOnLoseFocus}
                onChange={(e) => onChange({ pauseAudioOnLoseFocus: e.target.checked })}
                data-testid="voice-settings-pause-audio-on-lose-focus"
              />
              <span className="text-vibe-text">Pause audio playback</span>
            </label>
            <label
              className="flex items-center gap-2 mb-2"
              title="Currently affects the finalize-in-flight branch only. Active recorder is always released on tab switch via the resource-safety teardown helper."
            >
              <input
                type="checkbox"
                checked={settings.stopRecorderOnLoseFocus}
                onChange={(e) => onChange({ stopRecorderOnLoseFocus: e.target.checked })}
                data-testid="voice-settings-stop-recorder-on-lose-focus"
              />
              <span className="text-vibe-text">Stop active recorder (rare cases only — see tooltip)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.resetStatusOnLoseFocus}
                onChange={(e) => onChange({ resetStatusOnLoseFocus: e.target.checked })}
                data-testid="voice-settings-reset-status-on-lose-focus"
              />
              <span className="text-vibe-text">Reset status to idle</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}


export function VoicePanel() {
  // Shell-owned slice — fields needed for VoiceSettingsModal mount + loadVoices
  // useEffect. Other UI fields read by VoiceControlsBar / ToolProgressStrip /
  // VoiceHistoryLog directly (smart-subcomponent pattern post-Phase-3 decomp).
  const {
    settings, voices, models, status,
    teardownStreamingSession,
    updateSettings,
    loadVoices,
    loadModels,
  } = useVoiceStore();

  // WS-A Φ3 — tenant + voice tabs lifecycle. tenantId drives:
  //   1. voiceTabsStore hydration (per-tenant tab metadata in localStorage)
  //   2. voiceStore.hydrate (per-tenant byTabId settings v2 + legacy migration)
  //   3. auto-creation of first voice tab on cold-start (mirrors ChatPanelPi pattern)
  const tenantId = useTenantStore((s) => s.tenantId);
  const activeTabId = useVoiceTabsStore((s) => s.activeTabId);
  const hydrateVoiceTabs = useVoiceTabsStore((s) => s.hydrate);
  const newVoiceTab = useVoiceTabsStore((s) => s.newTab);
  const hydrateVoiceSettings = useVoiceStore((s) => s.hydrate);

  // 1.134.0 — proactive Lockdown Mode detection. Captured ONCE on first
  // render (state init function) because the heuristic is stable across
  // a session — Lockdown Mode can't be toggled mid-page-load. Avoids
  // re-evaluating navigator/window on every render.
  const [lockdownLikely] = useState(() => isLockdownLikelyBlocked());
  // 2026-05-07: history default-open so users see the transcript+reply
  // chat log as turns land. They can collapse manually.
  const [historyOpen, setHistoryOpen] = useState(true);
  // 1.166.0: settings modal open state lifted into uiStore so
  // VoiceAgentQuickSwitcher's per-row icon can open it (after switching
  // to the row's tab). Transient (not persisted).
  const settingsOpen = useUIStore((s) => s.voiceSettingsOpen);
  const setSettingsOpen = useUIStore((s) => s.setVoiceSettingsOpen);
  // Phase 9.0.1 — vibe:focus-panel listener target. Attached to the
  // VoiceControlsBar's main div (data-testid="voice-panel"). VoiceTabsStrip
  // renders ABOVE in the Fragment so a `block:'start'` scroll puts the
  // strip above the viewport fold by design — the "voice panel" intent is
  // to land the user on the controls (orb/PTT/status), not the tab strip nav.
  const sectionRef = useRef<HTMLDivElement | null>(null);

  // F1 — load live voice list from qwen3-tts on first mount. On error,
  // store retains the VOICE_PRESETS fallback so the dropdown still works.
  useEffect(() => {
    void loadVoices();
  }, [loadVoices]);

  // 2026-06-28 — load tenant-router models for the voice model picker on
  // mount (same /chat-proxy/v1/models source Pi Chat uses). Best-effort; on
  // failure the picker shows just "Auto".
  useEffect(() => {
    void loadModels();
  }, [loadModels]);

  // Phase 9.0.1 — voice agent focus_panel('voice') listener. Same shape as
  // KnowledgeAskBox + KnowledgeIngestPanel listeners (Phase 9.0). Early-
  // return on non-'voice' panel so coexisting handlers don't cross-fire.
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ panel?: string }>;
      if (ev.detail?.panel !== 'voice') return;
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('vibe:focus-panel', handler);
    return () => window.removeEventListener('vibe:focus-panel', handler);
  }, []);

  // WS-A Φ3 — on tenant change, hydrate voiceTabsStore + voiceStore, then
  // auto-create the first voice tab if cold start. Mirrors ChatPanelPi
  // useEffect A pattern (ChatPanelPi.tsx:402-422).
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        await hydrateVoiceTabs(tenantId);
        if (cancelled) return;
        const tabsAfterHydrate = useVoiceTabsStore.getState().tabs;
        const tabIds = tabsAfterHydrate.map((t) => t.id);
        // 1.166.2: pass persisted activeTabId as hint so voiceStore.activeTabId
        // points at the user's last-active tab AT hydrate-time. Pre-1.166.2,
        // hydrate defaulted activeTabId to tabsForTenant[0], and the post-
        // hydrate bridge below resynced it via setActiveTab — but a user
        // click during that async window fired fireInitialGreetingIfReady
        // against Tab 0's settings (Bridge), not the persisted active tab.
        const persistedActiveTabId = useVoiceTabsStore.getState().activeTabId;
        await hydrateVoiceSettings(
          tenantId,
          tabIds.length > 0 ? tabIds : undefined,
          persistedActiveTabId,
        );
        if (cancelled) return;
        // Cold-start: no persisted tabs → create one so the strip has content
        // AND voiceStore.byTabId has a matching entry. hydrateVoiceSettings's
        // legacy-migration path created a 'tab-0' byTabId entry; align tab
        // metadata to it.
        if (useVoiceTabsStore.getState().tabs.length === 0) {
          // Allocate a fresh tab via voiceTabsStore (uuid), then mirror it
          // into voiceStore.byTabId. After cold start, voiceStore had a
          // sentinel 'tab-0' from legacy migration — we drop that and use
          // the voiceTabsStore-allocated uuid as the canonical id.
          const fresh = newVoiceTab(tenantId);
          if (fresh) {
            // Re-hydrate voiceStore with the canonical tab id so byTabId
            // is keyed by the real uuid.
            await hydrateVoiceSettings(tenantId, [fresh.id]);
          }
        }
        // WS-A Φ6 R3 re-review C-B fix — re-sync bridge AFTER hydrate completes
        // so the auto-create branch in voiceStore.setActiveTab (if it fired
        // racily with the bridge useEffect at mount time, before hydrate populated
        // byTabId) gets superseded by the hydrated settings. Idempotent: if the
        // bridge already fired with the correct byTabId entry, this is a no-op
        // via the `activeTabId === tabId` guard.
        const liveActiveTabId = useVoiceTabsStore.getState().activeTabId;
        if (liveActiveTabId && !cancelled) {
          // Force voiceStore.activeTabId to flip back via temporary nulling so
          // the guard does NOT short-circuit. Simpler: call setState with the
          // tab id explicitly to ensure byTabId[id] holds hydrated settings.
          const liveState = useVoiceStore.getState();
          if (liveState.activeTabId === liveActiveTabId) {
            // Same id but byTabId entry may have been auto-created with stale
            // mirror settings; reset activeTabId so setActiveTab re-runs the
            // mirror swap with current byTabId[id] (which hydrate just populated).
            useVoiceStore.setState({ activeTabId: null });
          }
          liveState.setActiveTab(tenantId, liveActiveTabId);
        }
      } catch (err) {
        // hydrate failures are non-fatal (legacy in-memory state stays usable),
        // but per Rule #19 — never silent: log so a real-device regression is
        // visible at devtools level instead of disappearing into the void.

        console.error('[VoicePanel] voice tabs/settings hydrate failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, hydrateVoiceTabs, hydrateVoiceSettings, newVoiceTab]);

  // 1.166.0: WS-A Φ3 + D-A11's auto-close-on-tab-switch REMOVED. With
  // VoiceAgentQuickSwitcher's per-row settings icon (which switches tab
  // THEN opens settings), the auto-close fought that flow — activeTabId
  // changed first, useEffect re-closed, and the modal never opened. Modal
  // now stays open through tab switches and re-renders with the new active
  // tab's settings (settings/voices/status are all read off activeTabId).

  // WS-A Φ6 R3 C-1 fix — BRIDGE voiceTabsStore.activeTabId → voiceStore.
  // setActiveTab so the per-turn teardown (D-A3) + greeting fire (D-A6) +
  // outputAudio pause-not-recreate (D-A8) + top-level mirror swap actually
  // fire on every UI tab click. Pre-fix, VoiceTabsStrip.onClick only updated
  // voiceTabsStore.activeTabId; voiceStore.activeTabId never changed →
  // byTabId reads kept routing to the original tab forever, and the entire
  // setActiveTab teardown discipline was dead production code.
  //
  // voiceStore.setActiveTab is idempotent (no-op when activeTabId === tabId
  // or tabId is falsy), so the initial mount + every voiceTabsStore.activeTabId
  // change converge into a single setActiveTab call.
  useEffect(() => {
    if (!tenantId || !activeTabId) return;
    useVoiceStore.getState().setActiveTab(tenantId, activeTabId);
  }, [tenantId, activeTabId]);

  return (
    // Flex COLUMN so the tabs strip + controls bar + tool strip stay pinned
    // (intrinsic height) and ONLY the transcript log flexes + scrolls. The
    // parent ResizableVoicePanel wrapper clips (overflow:hidden), so this is
    // the single scroll surface. (2026-07-03 — operator: "the control buttons
    // for voice shouldn't scroll up / only the text chat area should have scroll".)
    <div className="flex min-h-0 h-full flex-col">
      {/* WS-A Φ3 D-A4 — voice tabs strip ABOVE the panel header. Returns
          null when no tabs OR pre-hydrate tenantId, so a single-tab user
          sees the same chrome as pre-1.163.0. */}
      <VoiceTabsStrip tenantId={tenantId} />
      <VoiceControlsBar
        historyOpen={historyOpen}
        setHistoryOpen={setHistoryOpen}
        sectionRef={sectionRef}
        lockdownLikely={lockdownLikely}
      />
      {/* Phase 9.3 (2026-05-24) — tool_progress activity strip. Renders
          one entry per emitted backend `tool_progress` WS frame, cleared
          on end_of_turn (CONC-9.3-3 via writeIdleToOriginTab). */}
      <ToolProgressStrip />
      {historyOpen && <VoiceHistoryLog />}
      {settingsOpen && (
        <VoiceSettingsModal
          settings={settings}
          voices={voices}
          models={models}
          status={status}
          onClose={() => setSettingsOpen(false)}
          onChange={updateSettings}
          onTeardownStreaming={teardownStreamingSession}
        />
      )}
    </div>
  );
}
